import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { EpcEstimateEquipmentBuildUpPanel } from './EpcEstimateEquipmentBuildUpPanel';
import { getEst3CostProvenance } from './est3CostProvenance';
import { EST3_EQUIPMENT_OWNERSHIP_MODES } from './est3EquipmentKinds';

const { Text } = Typography;

export interface EpcEstimateEquipmentWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  equipmentLines: any[];
  loading?: boolean;
  onOpenAdd?: () => void;
  equipmentClasses: any[];
  onCostingChanged?: () => void;
}

/**
 * EST3 UX2 Wave 5 — Equipment depth: master-detail lines + plant build-up + class library.
 */
export const EpcEstimateEquipmentWorkbench: React.FC<EpcEstimateEquipmentWorkbenchProps> = ({
  estimateId,
  revisionId,
  readOnly,
  equipmentLines,
  loading,
  onOpenAdd,
  equipmentClasses,
  onCostingChanged,
}) => {
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [equipRateForm] = Form.useForm();

  const selectedLine = useMemo(
    () => equipmentLines.find((l) => l.id === selectedLineId) || null,
    [equipmentLines, selectedLineId]
  );

  const totalEquipment = useMemo(
    () => equipmentLines.reduce((acc, l) => acc + (Number(l.total_amount) || 0), 0),
    [equipmentLines]
  );

  const equipRateM = useMutation({
    mutationFn: (v: Record<string, unknown>) => epcEstimationService.upsertEquipmentRate(estimateId, v),
    onSuccess: () => equipRateForm.resetFields(),
  });

  return (
    <Card size="small" title="Equipment">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {readOnly ? (
          <Alert type="info" showIcon message="Approved — equipment read-only" />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Statistic title="Equipment lines" value={equipmentLines.length} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Equipment cost total"
              value={totalEquipment}
              formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Equipment classes" value={equipmentClasses.length} />
          </Col>
        </Row>

        {!readOnly && onOpenAdd ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenAdd}>
            Add equipment cost line
          </Button>
        ) : null}

        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              dataSource={equipmentLines}
              pagination={equipmentLines.length > 25 ? { pageSize: 25 } : false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedLineId ? [selectedLineId] : [],
                onChange: (keys) => setSelectedLineId((keys[0] as string) || null),
              }}
              onRow={(row) => ({
                onClick: () => setSelectedLineId(row.id),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                {
                  title: 'Total',
                  dataIndex: 'total_amount',
                  width: 100,
                  render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                },
                {
                  title: 'Prov.',
                  width: 100,
                  ellipsis: true,
                  render: (_: unknown, row: any) => {
                    const p = getEst3CostProvenance(row);
                    return p.summary !== '—' ? <Tag color="blue">{p.summary}</Tag> : '—';
                  },
                },
              ]}
            />
          </Col>
          <Col xs={24} lg={14}>
            <EpcEstimateEquipmentBuildUpPanel
              estimateId={estimateId}
              revisionId={revisionId}
              costLineId={selectedLineId}
              costLine={selectedLine}
              readOnly={readOnly}
              onChanged={onCostingChanged}
            />
          </Col>
        </Row>

        <Card type="inner" size="small" title="Company equipment classes">
          <Table
            size="small"
            rowKey="id"
            dataSource={equipmentClasses}
            pagination={equipmentClasses.length > 10 ? { pageSize: 10 } : false}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 90 },
              { title: 'Name', dataIndex: 'name' },
              { title: 'Mode', dataIndex: 'ownership_mode', width: 100 },
            ]}
          />
          {!readOnly ? (
            <Form
              form={equipRateForm}
              layout="inline"
              onFinish={(v) => equipRateM.mutate(v)}
              style={{ marginTop: 12 }}
            >
              <Form.Item name="equipmentClassId" rules={[{ required: true }]}>
                <Select
                  placeholder="Class"
                  style={{ width: 160 }}
                  options={equipmentClasses.map((c: any) => ({ value: c.id, label: c.code }))}
                />
              </Form.Item>
              <Form.Item name="ownershipMode" initialValue="RENTED">
                <Select
                  style={{ width: 110 }}
                  options={EST3_EQUIPMENT_OWNERSHIP_MODES.map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
              <Form.Item name="rate" rules={[{ required: true }]}>
                <InputNumber min={0} placeholder="Rate" />
              </Form.Item>
              <Button htmlType="submit" loading={equipRateM.isPending}>
                Save rate
              </Button>
            </Form>
          ) : (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Equipment rates seed component build-up when rate is left blank.
            </Text>
          )}
        </Card>
      </Space>
    </Card>
  );
};
